import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, storage } from '../firebase';

export interface UploadedLogo {
  id: string;
  name: string;
  url: string;
  uploadedAt: Date;
  size: number;
  type: string;
}

export class LogoService {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  private static readonly LOGOS_PATH = 'logos';

  /**
   * Validate uploaded file
   */
  static validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size must be less than ${this.MAX_FILE_SIZE / 1024 / 1024}MB`
      };
    }

    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Only image files (JPEG, PNG, GIF, WebP, SVG) are allowed'
      };
    }

    return { valid: true };
  }

  /**
   * Upload logo file to Firebase Storage
   */
  static async uploadLogo(file: File): Promise<UploadedLogo> {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('You must be signed in to upload a logo');

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Generate unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const fileName = `${timestamp}.${fileExtension}`;
      const filePath = `${this.LOGOS_PATH}/${userId}/${fileName}`;

      // Create storage reference
      const storageRef = ref(storage, filePath);

      // Add metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          uploadedBy: userId,
          uploadedAt: new Date().toISOString()
        }
      };

      // Upload file
      const snapshot = await uploadBytes(storageRef, file, metadata);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      const uploadedLogo: UploadedLogo = {
        id: `${userId}/${fileName}`,
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date(),
        size: file.size,
        type: file.type
      };

      return uploadedLogo;
    } catch (error) {
      console.error('❌ Logo upload failed:', error);
      throw error;
    }
  }

  /**
   * Delete logo from Firebase Storage
   */
  static async deleteLogo(logoId: string): Promise<void> {
    try {
      const filePath = `${this.LOGOS_PATH}/${logoId}`;
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('❌ Logo deletion failed:', error);
      throw error;
    }
  }

  /**
   * Generate optimized logo URL for QR codes
   */
  static getOptimizedLogoUrl(originalUrl: string, size: number = 200): string {
    // For Firebase Storage URLs, we can add parameters for optimization
    // This is a basic implementation - Firebase doesn't have built-in image optimization
    // You could integrate with a service like Cloudinary or implement custom optimization
    return originalUrl;
  }

  /**
   * Get file extension from URL
   */
  static getFileExtension(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const extension = pathname.split('.').pop()?.toLowerCase() || '';
      return extension;
    } catch {
      return '';
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
